<script setup>
import { useDetailsReport } from '#/detailsReport/useDetailsReport'
import InfoLine from '#/detailsReport/InfoLine'
import { useFilters } from '#/general/useFilters'
import TooltipContainer from '#/general/TooltipContainer'
import PotentialAnalysisProgressLine from '#/detailsReport/PotentialAnalysisProgressLine'
import AppMenu from '#/general/AppMenu'
import { inject } from 'vue'
import PersistentTooltip from '#/general/PersistentTooltip'

const { potential } = useDetailsReport()
const { prettifyNumber } = useFilters()
const { pages, global, screen } = inject('Shared')
</script>

<template lang="pug">
.potentialTableContainer
  .potential-table--header
    .potential-table--title {{ global.potentialVolumeLabel }}
    .potential-table--tooltip
      persistent-tooltip(:content-width="`${screen.gt.xs ? 450 : 300}px`" :title="global.potentialVolumeLabel" :offset="screen.gt.xs ? [0, 0] : [-250, -20]")
        template(#chip)
          svg(width='22' height='22' viewBox='0 0 22 22' fill='none' xmlns='http://www.w3.org/2000/svg')
            circle(cx='11' cy='11' r='11' fill='#EB604F')
            path(d='M10.0953 9.624H11.9033V14H10.0953V9.624ZM10.9993 9.272C10.674 9.272 10.41 9.18667 10.2073 9.016C10.01 8.84 9.91131 8.61867 9.91131 8.352C9.91131 8.08533 10.01 7.86667 10.2073 7.696C10.41 7.52 10.674 7.432 10.9993 7.432C11.33 7.432 11.594 7.51467 11.7913 7.68C11.9886 7.84533 12.0873 8.05867 12.0873 8.32C12.0873 8.59733 11.9886 8.82667 11.7913 9.008C11.594 9.184 11.33 9.272 10.9993 9.272Z' fill='white')
            circle(cx='11' cy='11' r='6.4' stroke='white' stroke-width='1.2')
            circle(cx='11' cy='11' r='6.4' stroke='white' stroke-width='1.2')
        template(#icon)
          svg(width='22' height='22' viewBox='0 0 22 22' fill='none' xmlns='http://www.w3.org/2000/svg')
            circle(cx='11' cy='11' r='9.4' fill='#EB604F' stroke='white' stroke-width='1.2')
            path(d='M9.6 16V9.436H12.312V16H9.6ZM10.956 8.908C10.46 8.908 10.064 8.776 9.768 8.512C9.472 8.248 9.324 7.92 9.324 7.528C9.324 7.136 9.472 6.808 9.768 6.544C10.064 6.28 10.46 6.148 10.956 6.148C11.452 6.148 11.848 6.272 12.144 6.52C12.44 6.768 12.588 7.088 12.588 7.48C12.588 7.896 12.44 8.24 12.144 8.512C11.848 8.776 11.452 8.908 10.956 8.908Z' fill='white')
        template(#body)
          div(v-html="global.potentialVolumeTooltipBody")
  .lt-lg
    .potentialTableItemMobileContainer
      app-menu(position="bottom" content-width="350px" tooltip-mode attach-to-parent)
        template(#parent)
          .itemContainerTitle Gebäudevolumen
        template(#content)
          tooltip-container(title="Gebäudevolumen" body="Unter Gebäudevolumen wird das bestehende, oberirdische Gebäudevolumen sowie das noch realisierbare Restvolumen angezeigt.")
      potential-analysis-progress-line(
        v-if="potential.analysis && potential.analysis.potentialVolume && potential.parcel && potential.parcel.currentVolume"
        :volume="potential.parcel.currentVolume"
        :potential-volume="potential.analysis.potentialVolume"
      )
      .row.full-width.justify-between.q-mb-md.q-pt-sm
        info-line(label="Gebäudevolumen Ist-Zustand" :show-box="true" style="justify-content: center" box-color="#DCE7F2")
        .flex.flex-center
          .currentValue(v-if="potential.parcel && potential.parcel.currentVolume") {{ prettifyNumber(potential.parcel.currentVolume, 1) }}m
            .smallText 3
        .row.full-width.justify-between
          info-line(label="Gebäudevolumen Potenzial" style="justify-content: center" :show-box="true" box-color="#5FEFC0")
          .flex.flex-center
            .potentialValue(v-if="potential.analysis && potential.analysis.potentialVolume > 0") {{ prettifyNumber(potential.analysis.potentialVolume, 1) }}m
              .smallText 3

    .potentialTableItemMobileContainer
      app-menu(position="bottom" content-width="350px" tooltip-mode attach-to-parent)
        template(#parent)
          .itemContainerTitle Geschosse:
        template(#content)
          tooltip-container(title="Geschosse" body="Hier werden die vorhandenen Geschosse sowie die gemäss Baugesetz zusätzlich möglichen Geschosse (Potenzial) angezeigt. Keller- sowie Dachgeschosse gelten nicht als Vollgeschosse und werden nicht eingerechnet.")
      potential-analysis-progress-line(
        v-if="potential.analysis"
        :volume="potential.parcel.avgFloors"
        :potential-volume="potential.analysis.potentialFloors"
      )
      .row.full-width.justify-between
        info-line(label="Geschosse Ist-Zustand" :show-box="true" style="justify-content: center" box-color="#DCE7F2")
        .currentValue {{ potential.parcel.avgFloors }}
      .row.full-width.justify-between
        info-line(label="Geschosse Potenzial" style="justify-content: center" :show-box="true" box-color="#5FEFC0")
        .flex.flex-center(v-if="potential.analysis")
          span.potentialValue {{ prettifyNumber(potential.analysis.potentialFloors, 0) }}

    .potentialTableItemMobileContainer
      app-menu(position="bottom" content-width="350px" tooltip-mode attach-to-parent)
        template(#parent)
          .itemContainerTitle Fassadenhöhe:
        template(#content)
          tooltip-container(title="Fassadenhöhe" body="Hier wird die aktuelle Fassadenhöhe (Traufhöhe) sowie die mögliche Resthöhe (Potenzial) bis zur baurechtlich maximal erlaubten Fassadenhöhe angezeigt.")
      potential-analysis-progress-line(
        v-if="potential.parcel && potential.parcel.avgFacadeHeight && potential.analysis && potential.analysis.potentialFacadeBuildingHeight"
        :volume="potential.parcel.avgFacadeHeight"
        :potential-volume="potential.analysis.potentialFacadeBuildingHeight"
      )
      .row.full-width.justify-between
        info-line(label="Fassadenhöhe Ist-Zustand" :show-box="true" style="justify-content: center" box-color="#DCE7F2")
        .currentValue(v-if="potential.parcel && potential.parcel.avgFacadeHeight") {{ potential.parcel.avgFacadeHeight + 'm' }}
      .row.full-width.justify-between
        info-line(label="Fassadenhöhe Potenzial" style="justify-content: center" :show-box="true" box-color="#5FEFC0")
        .flex.flex-center
          .potentialValue(v-if="potential.analysis && potential.analysis.potentialFacadeBuildingHeight") {{ prettifyNumber(potential.analysis.potentialFacadeBuildingHeight, 1) }}m

  table.gt-md.potentialTable
    tr
      th.normalHeaderSize
      th.normalHeaderSize
        .headerContent
          info-line(label="Ist-Zustand" :show-box="true" style="justify-content: center" box-color="#DCE7F2" tooltip-text="Der Ist-Zustand gibt Auskunft über den aktuellen, baulichen Zustand der Liegenschaft." :hide-tooltip="true")
            template(#tooltipContent)
              tooltip-container(title="Ist-Zustand" body="Der Ist-Zustand gibt Auskunft über den aktuellen, baulichen Zustand der Liegenschaft." )
      th.normalHeaderSize
        .headerContent
          info-line(label="Potenzial" style="justify-content: center" tooltip-text="Das Potenzial zeigt die vorhandenen Reserven bzw. das Delta bis zum baurechtlich erlaubten Maximum auf." :hide-tooltip="true")
            template(#tooltipContent)
              tooltip-container(title="Potenzial" body="Das Potenzial zeigt die vorhandenen Reserven bzw. das Delta bis zum baurechtlich erlaubten Maximum auf." )
      th
    tr
      td
        info-line(label="Gebäudevolumen" tooltip-text="Unter Gebäudevolumen wird das bestehende, oberirdische Gebäudevolumen (grau) sowie das noch realisierbare Restvolumen (grün) angezeigt.")
      td
        .flex.flex-center
          .currentValue(v-if="potential.parcel && potential.parcel.currentVolume") {{ prettifyNumber(potential.parcel.currentVolume, 1) }}m
            .smallText 3
      td(v-if="potential.analysis")
        .flex.flex-center
          .potentialValue(v-if="potential.analysis && potential.analysis.potentialVolume > 0") {{ prettifyNumber(potential.analysis.potentialVolume, 1) }}m
            .smallText 3
      td
        potential-analysis-progress-line(v-if="potential.analysis" :volume="potential.parcel.currentVolume" :potential-volume="potential.analysis.potentialVolume")
    tr
      td
        info-line(label="Geschosse" tooltip-text="Hier werden die vorhandenen Vollgeschosse (grau) sowie die gemäss Baugesetz zusätzlich möglichen Geschosse (grün) angezeigt. Keller- sowie Dachgeschosse gelten nicht als Vollgeschosse und werden nicht eingerechnet (weiss).")
      td
        .flex.flex-center
          .currentValue {{ Math.floor(potential.parcel.avgFloors) }}
      td
        .flex.flex-center(v-if="potential.analysis")
          .potentialValue {{ prettifyNumber(potential.analysis.potentialFloors, 0) }}
      td
        potential-analysis-progress-line(v-if="potential.analysis" :volume="potential.parcel.avgFloors" :potential-volume="potential.analysis.potentialFloors")
    tr
      td
        info-line(label="Fassadenhöhe" tooltip-text="Hier wird die aktuelle Fassadenhöhe (grau) sowie die mögliche Resthöhe (grün) bis zur baurechtlich maximal erlaubten Fassadenhöhe angezeigt.")
      td
        .flex.flex-center
          .currentValue(v-if="potential.parcel && potential.parcel.avgFacadeHeight") {{ potential.parcel.avgFacadeHeight }}m
      td
        .flex.flex-center
          .potentialValue(v-if="potential.analysis && potential.analysis.potentialFacadeBuildingHeight") {{ prettifyNumber(potential.analysis.potentialFacadeBuildingHeight, 1) }}m
      td(v-if="potential.parcel && potential.parcel.avgFacadeHeight && potential.analysis && potential.analysis.potentialFacadeBuildingHeight")
        potential-analysis-progress-line(:volume="potential.parcel.avgFacadeHeight" :potential-volume="potential.analysis.potentialFacadeBuildingHeight")
    tr
      td
      td
      td
      td
        .row
          .helperSign
            .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundHigh}`")
            .label hoch
          .helperSign
            .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundMedium}`")
            .label mittel
          .helperSign
            .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundLow}`")
            .label tief
</template>

<style scoped lang="sass">
.potentialTableContainer
  background: rgba(255, 255, 255, 0.7)
  border: 1px solid #D9D9D9
  box-sizing: border-box
  box-shadow: 0px 15px 30px rgba(166, 166, 166, 0.25)
  border-radius: 10px
  margin-bottom: 20px
  width: 100%
  @media screen and (min-width: 1440px)
    padding: 30px
  @media screen and (max-width: 1440px)
    padding: 30px 20px
  td
    padding: 20px !important
  .normalHeaderSize
    position: relative
    width: 200px
    .headerContent
      display: flex
      justify-content: center
      align-content: center
      width: 100%
  .potential-table--header
    display: flex
    align-items: center
    flex-direction: row
    @media screen and (min-width: 1440px)
      margin-bottom: 15px
    @media screen and (max-width: 1440px)
      margin-bottom: 30px
    .potential-table--title
      font-size: 16px
      line-height: 130%
      text-transform: uppercase
      color: #626C85
      @media screen and (min-width: 1440px)
        font-weight: bold
      @media screen and (max-width: 1440px)
        font-weight: 700
    .potential-table--tooltip
      margin-left: 10px
  .potentialTable
    overflow: visible !important
    width: 100%
  .currentValue
    position: relative
    @media screen and (min-width: 1440px)
      font-weight: 500
      font-size: 16px
      line-height: 130%
      color: #626C85
    @media screen and (max-width: 1440px)
      font-style: normal
      font-weight: 500
      font-size: 12px
      line-height: 130%
      text-align: right
      color: #626C85
  .potentialValue
    position: relative
    color: v-bind('global.graphicElementColor')
    @media screen and (min-width: 1440px)
      font-weight: bold
      font-size: 16px
      line-height: 130%
    @media screen and (max-width: 1440px)
      font-weight: 700
      font-size: 12px
      line-height: 130%
      text-align: right
      margin-top: 5px
  .smallText
    position: absolute
    top: -4px
    right: -7px
    font-size: 10px
  .helperSign
    display: flex
    flex-direction: row
    align-items: center
    margin-right: 12px
    .circle
      width: 16px
      height: 16px
      border-radius: 100%
      margin-right: 6px
    .label
      font-weight: 500
      font-size: 14px
      line-height: 130%
      color: #626C85
  .potentialTableItemMobileContainer
    padding-bottom: 30px
    .itemContainerTitle
      font-weight: 600
      font-size: 16px
      line-height: 200%
      color: #626C85
      @media screen and (min-width: 1440px)
        margin-bottom: 20px
</style>
